import {
  Fragment,
  lazy,
  startTransition,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { I18nProvider } from "next-intl";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { slugForPath } from "@/lib/pageTitle";
import { learningLocale } from "@/lib/public-learning-routes";
import { cancelWorkspaceDocumentScrollRestore } from "@/lib/workspace-document-scroll";
import { appendSearch, WORKSPACE_ROUTES } from "@/lib/workspace-routes";
import { StrategyTabsProvider } from "@/hooks/StrategyTabsProvider";
import { TickerRegistryProvider } from "@/hooks/TickerRegistryProvider";
import { TickerWorkspacesProvider } from "@/hooks/TickerWorkspacesProvider";
import { AdminKolsProvider } from "@/hooks/useAdminKols";
import { AdminNotificationsProvider } from "@/hooks/useAdminNotifications";
import { AdminUiProvider } from "@/hooks/useAdminUi";
import { AdminUsersProvider } from "@/hooks/useAdminUsers";
import { AlertsProvider } from "@/hooks/useAlerts";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { HolidaysProvider } from "@/hooks/useHolidays";
import { JournalEntriesProvider } from "@/hooks/useJournalEntries";
import { KolDashboardProvider } from "@/hooks/useKolDashboard";
import { LibraryProvider } from "@/hooks/useLibrary";
import { NotificationsProvider } from "@/hooks/useNotifications";
import { PositionsProvider } from "@/hooks/usePositions";
import { usePublicLearningMetadata } from "@/hooks/usePublicLearningMetadata";
import { PublicLearningTelemetryProvider } from "@/hooks/usePublicLearningTelemetry";
import { SavedStrategiesProvider } from "@/hooks/useSavedStrategies";
import { SimulatorTabsProvider } from "@/hooks/useSimulatorTabs";
import { StrategyUsageRecordsProvider } from "@/hooks/useStrategyUsageRecords";
import { SuperAdminsProvider } from "@/hooks/useSuperAdmins";
import { SuperEventsProvider } from "@/hooks/useSuperEvents";
import { SuperMetricsProvider } from "@/hooks/useSuperMetrics";
import { TelemetryProvider } from "@/hooks/useTelemetry";
import { useThemeClass } from "@/hooks/useThemeClass";
import { TransactionsProvider } from "@/hooks/useTransactions";
import { UserChannelProvider } from "@/hooks/useUserChannel";
import { useVersionFavicon } from "@/hooks/useVersionFavicon";
import { AppShell } from "@/components/layout/AppShell";
import { MarketingNavDrawer } from "@/components/layout/MarketingNavDrawer";
import { MarketingNavProvider } from "@/components/layout/MarketingNavProvider";
import { ThemeProvider } from "@/components/layout/ThemeProvider";
import { UpgradeModal } from "@/components/layout/UpgradeModal";
import { JsonLd } from "@/components/marketing/JsonLd";
import { InstallBanner } from "@/components/pwa/InstallBanner";
import { RequireAdmin } from "@/components/RequireAdmin";
import { RequireAuth } from "@/components/RequireAuth";
import { TooltipProvider } from "@/components/ui/tooltip";
import { V3_ROUTES, v3PathForLegacy } from "@/features/v3/routes";
import { loadWorkbenchBuild } from "@/features/workbench/build/load";
import { loadWorkbenchModel } from "@/features/workbench/model/load";
import { loadWorkbenchMonitor } from "@/features/workbench/monitor/load";
import {
  strategyCenterNavigationScrollTop,
  strategyCenterSectionFromPath,
  type StrategyCenterSection,
  type StrategyCenterSectionIntentHandler,
  type StrategyCenterSectionSelectHandler,
} from "@/features/workbench/shared/navigation";
import { Landing } from "@/pages/Landing";
import { Login } from "@/pages/Login";
import { NotFound } from "@/pages/NotFound";

const PublicOptionsTheory = lazy(() =>
  import("@/pages/PublicOptionsTheory").then((module) => ({
    default: module.PublicOptionsTheory,
  })),
);
const PublicStrategyImport = lazy(() =>
  import("@/pages/PublicStrategyImport").then((module) => ({
    default: module.PublicStrategyImport,
  })),
);

const V3Workspace = lazy(() =>
  import("@/features/v3/V3Workspace").then((module) => ({
    default: module.V3Workspace,
  })),
);
const GeneralWorkspace = lazy(() =>
  import("@/features/general/GeneralWorkspace").then((module) => ({
    default: module.GeneralWorkspace,
  })),
);
const StrategyCenterMonitor = lazy(loadWorkbenchMonitor);
const PublicManifold = lazy(() =>
  import("@/pages/PublicManifold").then((module) => ({
    default: module.PublicManifold,
  })),
);
const StrategyCenterThreeD = lazy(loadWorkbenchModel);
const WorkspaceSimEdit = lazy(loadWorkbenchBuild);

const AdminView = lazy(() =>
  import("@/components/admin/AdminView").then((module) => ({
    default: module.AdminView,
  })),
);
const About = lazy(() =>
  import("@/pages/About").then((module) => ({ default: module.About })),
);
const DemoScreen = lazy(() =>
  import("@/pages/DemoScreen").then((module) => ({
    default: module.DemoScreen,
  })),
);
const CreatorVerification = lazy(() =>
  import("@/pages/CreatorVerification").then((module) => ({
    default: module.CreatorVerification,
  })),
);
const KolDashboard = lazy(() =>
  import("@/pages/KolDashboard").then((module) => ({
    default: module.KolDashboard,
  })),
);
const KolLanding = lazy(() =>
  import("@/pages/KolLanding").then((module) => ({
    default: module.KolLanding,
  })),
);
const Learn = lazy(() =>
  import("@/pages/Learn").then((module) => ({ default: module.Learn })),
);
const PositionDetail = lazy(() =>
  import("@/pages/PositionDetail").then((module) => ({
    default: module.PositionDetail,
  })),
);
const Positions = lazy(() =>
  import("@/pages/Positions").then((module) => ({ default: module.Positions })),
);
const Privacy = lazy(() =>
  import("@/pages/Privacy").then((module) => ({ default: module.Privacy })),
);
const Purchase = lazy(() =>
  import("@/pages/Purchase").then((module) => ({ default: module.Purchase })),
);
const PurchaseSuccess = lazy(() =>
  import("@/pages/PurchaseSuccess").then((module) => ({
    default: module.PurchaseSuccess,
  })),
);
const QrScreen = lazy(() =>
  import("@/pages/QrScreen").then((module) => ({ default: module.QrScreen })),
);
const Safety = lazy(() =>
  import("@/pages/Safety").then((module) => ({ default: module.Safety })),
);
const Settings = lazy(() =>
  import("@/pages/Settings").then((module) => ({ default: module.Settings })),
);
const StrategyRecognition = lazy(() =>
  import("@/pages/StrategyRecognition").then((module) => ({
    default: module.StrategyRecognition,
  })),
);
const StrategyVolatilitySurface = lazy(() =>
  import("@/pages/StrategyRecognition").then((module) => ({
    default: module.StrategyVolatilitySurface,
  })),
);
const Terms = lazy(() =>
  import("@/pages/Terms").then((module) => ({ default: module.Terms })),
);
const Transactions = lazy(() =>
  import("@/pages/Transactions").then((module) => ({
    default: module.Transactions,
  })),
);
const Workspace = lazy(() =>
  import("@/pages/Workspace").then((module) => ({ default: module.Workspace })),
);
const WorkspaceDailyQuiz = lazy(() =>
  import("@/pages/WorkspaceDailyQuiz").then((module) => ({
    default: module.WorkspaceDailyQuiz,
  })),
);
const WorkspaceExploreSelection = lazy(() =>
  import("@/pages/WorkspaceExploreSelection").then((module) => ({
    default: module.WorkspaceExploreSelection,
  })),
);
const WorkspaceLibrary = lazy(() =>
  import("@/pages/WorkspaceLibrary").then((module) => ({
    default: module.WorkspaceLibrary,
  })),
);
const WorkspaceProfile = lazy(() =>
  import("@/pages/WorkspaceProfile").then((module) => ({
    default: module.WorkspaceProfile,
  })),
);
const WorkspaceInbox = lazy(() =>
  import("@/pages/WorkspaceInbox").then((module) => ({
    default: module.WorkspaceInbox,
  })),
);
const WorkspaceCommunityProfile = lazy(() =>
  import("@/pages/WorkspaceCommunityProfile").then((module) => ({
    default: module.WorkspaceCommunityProfile,
  })),
);
const WorkspaceExplore = lazy(() =>
  import("@/pages/WorkspaceExplore").then((module) => ({
    default: module.WorkspaceExplore,
  })),
);
// Authenticated chrome: RequireAuth gates access, NotificationsProvider
// scopes the bell, and AppShell provides the remaining shared header,
// portal, modal, and expiry-banner infrastructure around gated pages.
function GatedShell({ children }: { children: ReactNode }) {
  return (
    <RequireAuth flagship>
      <LibraryProvider>
        <JournalEntriesProvider deferInitialLoad>
          <NotificationsProvider>
            <AlertsProvider>
              <AppShell>{children}</AppShell>
            </AlertsProvider>
          </NotificationsProvider>
        </JournalEntriesProvider>
      </LibraryProvider>
    </RequireAuth>
  );
}

function WorkspaceGatedShell({ children }: { children: ReactNode }) {
  const { pathname, search } = useLocation();
  const { user } = useAuth();
  // 1.0 is admin-only: everyone else is sent to the matching 3.0 page.
  const v3Redirect =
    user && !user.admin ? v3PathForLegacy(pathname, search) : null;
  const creatorCommunityAccess =
    user?.verified_creator &&
    (pathname === WORKSPACE_ROUTES.me ||
      pathname === V3_ROUTES.profile ||
      pathname.startsWith("/workspace/explore/people/") ||
      pathname.startsWith(`${V3_ROUTES.gallery}/people/`));
  const usesTickerWorkspaces =
    pathname.startsWith("/workspace/workbench") ||
    pathname.startsWith("/workspace/general") ||
    pathname.startsWith("/workspace/v3");
  const deferLibrary = !pathname.startsWith(WORKSPACE_ROUTES.library);
  const deferJournalEntries = !pathname.startsWith(WORKSPACE_ROUTES.library);
  const shell = (
    <StrategyUsageRecordsProvider
      deferInitialLoad={pathname !== WORKSPACE_ROUTES.home}
    >
      <JournalEntriesProvider deferInitialLoad={deferJournalEntries}>
        {pathname.startsWith("/workspace/general") ? (
          <AlertsProvider>
            <AppShell lightweight>{children}</AppShell>
          </AlertsProvider>
        ) : (
          <PositionsProvider>
            <AlertsProvider>
              <AppShell lightweight>{children}</AppShell>
            </AlertsProvider>
          </PositionsProvider>
        )}
      </JournalEntriesProvider>
    </StrategyUsageRecordsProvider>
  );

  if (v3Redirect) return <Navigate replace to={v3Redirect} />;

  return (
    <RequireAuth
      flagship={
        !pathname.startsWith("/workspace/general") && !creatorCommunityAccess
      }
    >
      <NotificationsProvider>
        <LibraryProvider deferInitialLoad={deferLibrary}>
          <SavedStrategiesProvider>
            <SimulatorTabsProvider>
              {usesTickerWorkspaces ? (
                <TickerWorkspacesProvider>{shell}</TickerWorkspacesProvider>
              ) : (
                <StrategyTabsProvider>
                  <TickerWorkspacesProvider>{shell}</TickerWorkspacesProvider>
                </StrategyTabsProvider>
              )}
            </SimulatorTabsProvider>
          </SavedStrategiesProvider>
        </LibraryProvider>
      </NotificationsProvider>
    </RequireAuth>
  );
}

// `/` is dual-purpose: marketing landing for guests, /workspace for
// signed-in users. Wrapped here to stay inside AuthProvider so we can
// branch off the live session without flicker on cold load.
function RootRedirect() {
  const { status } = useAuth();
  const { search } = useLocation();
  const isMarketingPreview =
    import.meta.env.DEV && new URLSearchParams(search).has("marketing-preview");

  if (isMarketingPreview) return <Landing />;

  return status === "authenticated" ? (
    <Navigate to="/workspace/general/home" replace />
  ) : (
    <Landing />
  );
}

// Routes split out so tests can mount them under MemoryRouter without
// pulling in BrowserRouter's window dependency.
// Tiny shell that lets the theme hook live inside AuthProvider's
// scope. Returns null wrapper so it doesn't show up in the DOM.
function ThemeBridge({ children }: { children: ReactNode }) {
  useThemeClass();
  return <>{children}</>;
}

// Remounts everything below it on transitions that need a clean slate
// for per-user state — logout (authenticated → guest) and user-switch
// (authenticated A → authenticated B). Cables, SWR caches, and any
// provider that subscribes at mount get reset so no stale handles
// carry forward from a dead session.
//
// Crucially, the guest → authenticated transition (a fresh login from
// the unauthenticated marketing tree) does NOT remount: there are no
// per-user caches to flush yet, and a remount there would tear down
// the Login route mid-transition and replay every entry animation in
// the new tree before the router commits the navigate. We track the
// previous user id in a ref and only bump the generation key when the
// previous side of the transition was already authenticated.
//
// AuthProvider stays mounted above so `status` and `user` are stable
// across each transition.
function AuthScopedTree({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const prevIdRef = useRef<string | number | null>(null);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    const prev = prevIdRef.current;
    const curr = user?.id ?? null;
    if (prev === curr) return;
    if (prev !== null) {
      setGeneration((g) => g + 1);
    }
    prevIdRef.current = curr;
  }, [user?.id]);

  return <Fragment key={generation}>{children}</Fragment>;
}

// Sets document.title from the current path on every route change.
// Pages with dynamic content (Library article, Simulator strategy)
// override via useDocumentTitle in their own effects, which fire
// after this one because child effects run after parent effects.
function RouteTitleBridge() {
  const { pathname } = useLocation();
  useVersionFavicon(pathname);
  useDocumentTitle(slugForPath(pathname));
  usePublicLearningMetadata(pathname);
  return null;
}

const LAST_THREE_D_HREF_KEY = "abg:strategy-center:last-three-d";

function strategyCenterWorkspaceFromHref(href: string) {
  const queryIndex = href.indexOf("?");
  const search = queryIndex >= 0 ? href.slice(queryIndex) : "";
  return new URLSearchParams(search).get("workspace");
}

function readLastThreeDHref(workspaceId: string | null) {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.sessionStorage.getItem(
      `${LAST_THREE_D_HREF_KEY}:${workspaceId ?? "default"}`,
    );
    if (
      !stored?.startsWith(WORKSPACE_ROUTES.workbenchModel) ||
      strategyCenterWorkspaceFromHref(stored) !== workspaceId
    ) {
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

function writeLastThreeDHref(href: string) {
  if (typeof window === "undefined") return;
  try {
    const workspaceId = strategyCenterWorkspaceFromHref(href);
    window.sessionStorage.setItem(
      `${LAST_THREE_D_HREF_KEY}:${workspaceId ?? "default"}`,
      href,
    );
  } catch {
    // Session storage may be unavailable in privacy-restricted contexts.
  }
}

function hasExplicitThreeDSource(href: string) {
  const queryIndex = href.indexOf("?");
  if (queryIndex < 0) return false;
  const params = new URLSearchParams(href.slice(queryIndex));
  const source = params.get("source");
  if (source === "position") return params.has("position");
  if (source === "strategy" || source === "workspace") {
    return params.has("template");
  }
  return false;
}

function StrategyCenterSurfaceFallback() {
  return (
    <div
      aria-hidden
      className="flex min-h-[calc(100dvh-5rem)] w-full justify-center pt-32 sm:min-h-dvh"
    >
      <span className="size-5 animate-spin rounded-full border border-white/10 border-t-white/55" />
    </div>
  );
}

/**
 * One stable route tree for every Strategy Center section. The visible section
 * changes urgently while URL synchronization runs as a transition; visited
 * surfaces stay mounted at their full width so charts retain their measured
 * dimensions and calculation state.
 */
function StrategyCenterWorkspace() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const routedSection = strategyCenterSectionFromPath(pathname);
  const routedHref = `${pathname}${search}`;
  const routedWorkspaceId = strategyCenterWorkspaceFromHref(routedHref);
  const [initialLastThreeDHref] = useState(() =>
    routedSection === "three-d"
      ? routedHref
      : readLastThreeDHref(routedWorkspaceId),
  );
  const lastThreeDHrefRef = useRef<string | null>(initialLastThreeDHref);
  useEffect(() => {
    if (routedSection === "three-d") {
      lastThreeDHrefRef.current = routedHref;
      writeLastThreeDHref(routedHref);
    }
  }, [routedHref, routedSection]);
  const [pendingSection, setPendingSection] = useState<{
    href: string;
    pathname: string;
    section: StrategyCenterSection;
  } | null>(null);
  if (pendingSection?.pathname === pathname) setPendingSection(null);
  const activeSection = pendingSection?.section ?? routedSection;
  const sectionScrollPositionsRef = useRef(
    new Map<StrategyCenterSection, number>(),
  );
  const previousSectionRef = useRef<StrategyCenterSection | null>(null);
  const sectionNavigationScrollRef = useRef<{
    pathname: string;
    section: StrategyCenterSection;
    top: number;
  } | null>(null);

  useLayoutEffect(() => {
    const previousSection = previousSectionRef.current;
    if (previousSection && previousSection !== activeSection) {
      sectionScrollPositionsRef.current.set(previousSection, window.scrollY);
    }
    const activeHref =
      pendingSection?.section === activeSection
        ? pendingSection.href
        : routedHref;
    const explicitAnalysis =
      activeSection === "three-d" && hasExplicitThreeDSource(activeHref);
    const navigationScroll = sectionNavigationScrollRef.current;
    const navigationScrollApplies = navigationScroll?.section === activeSection;
    const navigationScrollCommitted =
      navigationScrollApplies &&
      pendingSection == null &&
      navigationScroll.pathname === pathname;
    const targetTop =
      navigationScrollApplies && navigationScroll
        ? navigationScroll.top
        : explicitAnalysis
          ? 0
          : (sectionScrollPositionsRef.current.get(activeSection) ?? 0);
    if (
      previousSection !== activeSection ||
      explicitAnalysis ||
      navigationScrollCommitted
    ) {
      cancelWorkspaceDocumentScrollRestore();
      window.scrollTo({ behavior: "auto", left: 0, top: targetTop });
    }
    previousSectionRef.current = activeSection;

    if (!navigationScrollCommitted || !navigationScroll) return;

    const root = document.documentElement;
    const previousOverflowAnchor = root.style.overflowAnchor;
    root.style.overflowAnchor = "none";
    let settleFrame: number | null = null;
    const commitFrame = window.requestAnimationFrame(() => {
      window.scrollTo({ behavior: "auto", left: 0, top: targetTop });
      settleFrame = window.requestAnimationFrame(() => {
        window.scrollTo({ behavior: "auto", left: 0, top: targetTop });
        if (sectionNavigationScrollRef.current === navigationScroll) {
          sectionNavigationScrollRef.current = null;
        }
        root.style.overflowAnchor = previousOverflowAnchor;
      });
    });

    return () => {
      window.cancelAnimationFrame(commitFrame);
      if (settleFrame != null) window.cancelAnimationFrame(settleFrame);
      root.style.overflowAnchor = previousOverflowAnchor;
    };
  }, [activeSection, pathname, pendingSection, routedHref]);

  const [visitedSections, setVisitedSections] = useState(
    () => new Set<StrategyCenterSection>([routedSection]),
  );
  if (!visitedSections.has(activeSection)) {
    setVisitedSections((current) => new Set(current).add(activeSection));
  }

  const [retainedSection, setRetainedSection] = useState<
    Exclude<StrategyCenterSection, "simulator">
  >(routedSection === "three-d" ? "three-d" : "strategies");
  if (activeSection !== "simulator" && activeSection !== retainedSection) {
    setRetainedSection(activeSection);
  }

  const preloadSection = useCallback<StrategyCenterSectionIntentHandler>(
    (section) => {
      if (section === "simulator") void loadWorkbenchBuild();
      else if (section === "strategies") void loadWorkbenchMonitor();
      else void loadWorkbenchModel();
    },
    [],
  );

  const selectSection = useCallback<StrategyCenterSectionSelectHandler>(
    (section, href) => {
      if (section === activeSection) {
        if (section === "three-d") return;
        if (href === `${pathname}${search}`) return;
        startTransition(() => navigate(href));
        return;
      }
      const requestedWorkspace = strategyCenterWorkspaceFromHref(href);
      const inMemoryThreeDHref = lastThreeDHrefRef.current;
      const rememberedThreeDHref =
        inMemoryThreeDHref &&
        strategyCenterWorkspaceFromHref(inMemoryThreeDHref) ===
          requestedWorkspace
          ? inMemoryThreeDHref
          : readLastThreeDHref(requestedWorkspace);
      const rememberedWorkspace = rememberedThreeDHref
        ? strategyCenterWorkspaceFromHref(rememberedThreeDHref)
        : null;
      const nextHref =
        section === "three-d" &&
        !hasExplicitThreeDSource(href) &&
        rememberedThreeDHref &&
        requestedWorkspace === rememberedWorkspace
          ? rememberedThreeDHref
          : href;
      preloadSection(section);
      const nextPathname = nextHref.split("?", 1)[0];
      const navigationScrollTop = strategyCenterNavigationScrollTop(
        activeSection,
        section,
      );
      sectionNavigationScrollRef.current =
        navigationScrollTop == null
          ? null
          : {
              pathname: nextPathname,
              section,
              top: navigationScrollTop,
            };
      setPendingSection({ href: nextHref, pathname: nextPathname, section });
      startTransition(() => navigate(nextHref));
    },
    [activeSection, navigate, pathname, preloadSection, search],
  );

  const simulatorActive = activeSection === "simulator";
  const contentActive = !simulatorActive;
  const hasVisitedSimulator = visitedSections.has("simulator");
  const hasVisitedContent =
    visitedSections.has("strategies") || visitedSections.has("three-d");

  return (
    <div className="relative min-h-[calc(100dvh-5rem)] w-full sm:min-h-dvh">
      {hasVisitedSimulator ? (
        <div
          aria-hidden={!simulatorActive}
          className={
            simulatorActive
              ? "relative w-full"
              : "pointer-events-none invisible absolute inset-0 w-full overflow-hidden"
          }
        >
          <Suspense fallback={<StrategyCenterSurfaceFallback />}>
            <WorkspaceSimEdit
              activeSection={activeSection}
              onSectionIntent={preloadSection}
              onSectionSelect={selectSection}
            />
          </Suspense>
        </div>
      ) : null}
      {hasVisitedContent &&
      (contentActive || retainedSection === "strategies") ? (
        <div
          aria-hidden={!contentActive}
          className={
            contentActive
              ? "relative w-full"
              : "pointer-events-none invisible absolute inset-0 w-full overflow-hidden"
          }
        >
          <Suspense fallback={<StrategyCenterSurfaceFallback />}>
            {retainedSection === "three-d" ? (
              <StrategyCenterThreeD
                activeSection={activeSection}
                onSectionIntent={preloadSection}
                onSectionSelect={selectSection}
              />
            ) : (
              <StrategyCenterMonitor
                activeSection={activeSection}
                onSectionIntent={preloadSection}
                onSectionSelect={selectSection}
              />
            )}
          </Suspense>
        </div>
      ) : null}
    </div>
  );
}

function LegacyStrategySimulatorRedirect() {
  const { search } = useLocation();
  return (
    <Navigate replace to={appendSearch(WORKSPACE_ROUTES.workbench, search)} />
  );
}

function WorkspaceRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate replace to={appendSearch(to, search)} />;
}

function LegacyCompoundRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set("panel", "compound");
  return <Navigate replace to={`${WORKSPACE_ROUTES.home}?${params}`} />;
}

function LegacyStrategyCenterRedirect() {
  const { pathname, search } = useLocation();
  const target = pathname.endsWith("/strategies")
    ? WORKSPACE_ROUTES.workbenchMonitor
    : pathname.endsWith("/three-d")
      ? WORKSPACE_ROUTES.workbenchModel
      : WORKSPACE_ROUTES.workbenchBuild;

  return <Navigate replace to={appendSearch(target, search)} />;
}

function LegacyAdminRedirect({ tab }: { tab?: string }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (tab) params.set("tab", tab);
  const nextSearch = params.size > 0 ? `?${params.toString()}` : "";
  return <Navigate replace to={`${WORKSPACE_ROUTES.admin}${nextSearch}`} />;
}

function WorkspaceAdminRoute() {
  return (
    <RequireAdmin>
      <AdminUsersProvider>
        <AdminNotificationsProvider>
          <AdminKolsProvider>
            <AdminUiProvider>
              <SuperMetricsProvider>
                <SuperEventsProvider>
                  <SuperAdminsProvider>
                    <AdminView />
                  </SuperAdminsProvider>
                </SuperEventsProvider>
              </SuperMetricsProvider>
            </AdminUiProvider>
          </AdminKolsProvider>
        </AdminNotificationsProvider>
      </AdminUsersProvider>
    </RequireAdmin>
  );
}

function AppRoutesContent() {
  const { pathname } = useLocation();
  return (
    <AuthProvider>
      <ThemeBridge>
        <AuthScopedTree>
          <TelemetryProvider>
            <UserChannelProvider>
              <TickerRegistryProvider>
                <ThemeProvider>
                  <I18nProvider localeOverride={learningLocale(pathname)}>
                    <PublicLearningTelemetryProvider>
                      <TooltipProvider delay={300}>
                        <HolidaysProvider>
                          <MarketingNavProvider>
                            <JsonLd />
                            <RouteTitleBridge />
                            <MarketingNavDrawer />
                            <UpgradeModal />
                            <Routes>
                              <Route path="/login" element={<Login />} />
                              <Route path="/purchase" element={<Purchase />} />
                              <Route
                                path="/purchase/success"
                                element={<PurchaseSuccess />}
                              />
                              <Route path="/k/:slug" element={<KolLanding />} />
                              <Route
                                path="/demo_screen"
                                element={<DemoScreen />}
                              />
                              <Route path="/qr_screen" element={<QrScreen />} />
                              <Route path="/about" element={<About />} />
                              <Route path="/philosophy" element={<Learn />} />
                              <Route
                                path="/zh-CN/learn/*"
                                element={<PublicOptionsTheory />}
                              />
                              <Route
                                path="/learn/*"
                                element={<PublicOptionsTheory />}
                              />
                              <Route
                                path="/strategies/iron-condor"
                                element={
                                  <Navigate
                                    to="/learn/strategies/iron-condor"
                                    replace
                                  />
                                }
                              />
                              <Route
                                path="/workspace/workbench/import-example"
                                element={
                                  <Navigate
                                    to={WORKSPACE_ROUTES.workbenchModel}
                                    replace
                                  />
                                }
                              />
                              <Route
                                path="/workspace/import/strategy"
                                element={
                                  <RequireAuth>
                                    <TickerWorkspacesProvider>
                                      <PublicStrategyImport />
                                    </TickerWorkspacesProvider>
                                  </RequireAuth>
                                }
                              />
                              <Route path="/safety" element={<Safety />} />
                              <Route path="/privacy" element={<Privacy />} />
                              <Route path="/terms" element={<Terms />} />
                              {/* 3.0 is the subscriber workspace; 1.0 is admin-only. */}
                              <Route
                                path="/workspace/v3/*"
                                element={
                                  <WorkspaceGatedShell>
                                    <V3Workspace />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/general/*"
                                element={
                                  <WorkspaceGatedShell>
                                    <GeneralWorkspace />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace"
                                element={
                                  <WorkspaceGatedShell>
                                    <Workspace />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/home"
                                element={
                                  <WorkspaceGatedShell>
                                    <Workspace />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/workbench"
                                element={
                                  <Navigate
                                    replace
                                    to={WORKSPACE_ROUTES.workbenchBuild}
                                  />
                                }
                              />
                              {[
                                WORKSPACE_ROUTES.workbenchBuild,
                                WORKSPACE_ROUTES.workbenchMonitor,
                                WORKSPACE_ROUTES.workbenchModel,
                              ].map((path) => (
                                <Route
                                  key={path}
                                  path={path}
                                  element={
                                    <WorkspaceGatedShell>
                                      <StrategyCenterWorkspace />
                                    </WorkspaceGatedShell>
                                  }
                                />
                              ))}
                              <Route
                                path="/workspace/explore"
                                element={
                                  <WorkspaceGatedShell>
                                    <WorkspaceExplore />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/explore/quiz"
                                element={
                                  <WorkspaceGatedShell>
                                    <WorkspaceDailyQuiz />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/explore/recognition"
                                element={
                                  <WorkspaceGatedShell>
                                    <StrategyRecognition />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/explore/surface"
                                element={
                                  <WorkspaceGatedShell>
                                    <StrategyVolatilitySurface />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/explore/selection"
                                element={
                                  <WorkspaceGatedShell>
                                    <WorkspaceExploreSelection />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/me/library"
                                element={
                                  <WorkspaceGatedShell>
                                    <WorkspaceLibrary />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/me/compound"
                                element={<LegacyCompoundRedirect />}
                              />
                              <Route
                                path="/workspace/library"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.library}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/me"
                                element={
                                  <WorkspaceGatedShell>
                                    <WorkspaceProfile />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/me/inbox"
                                element={
                                  <WorkspaceGatedShell>
                                    <WorkspaceInbox />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/explore/people/:id"
                                element={
                                  <WorkspaceGatedShell>
                                    <WorkspaceCommunityProfile />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/settings"
                                element={
                                  <WorkspaceGatedShell>
                                    <Settings />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/strategy-center/*"
                                element={<LegacyStrategyCenterRedirect />}
                              />
                              <Route
                                path="/workspace/build"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.explore}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/build/edit"
                                element={<LegacyStrategySimulatorRedirect />}
                              />
                              <Route
                                path="/workspace/build/manifold"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.workbenchModel}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/build/recognition"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.exploreRecognition}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/build/skew"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.exploreSurface}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/backtest"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.explore}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/backtest/run"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.explore}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/journal"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.library}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/journal/checklist"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.library}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/alerts"
                                element={
                                  <WorkspaceGatedShell>
                                    <Navigate
                                      replace
                                      to="/workspace#review-pane-alerts"
                                    />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path="/workspace/learn"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.explore}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/profile"
                                element={
                                  <WorkspaceRedirect to={WORKSPACE_ROUTES.me} />
                                }
                              />
                              <Route
                                path="/workspace/profile/settings"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.settings}
                                  />
                                }
                              />
                              <Route
                                path={WORKSPACE_ROUTES.admin}
                                element={
                                  <WorkspaceGatedShell>
                                    <WorkspaceAdminRoute />
                                  </WorkspaceGatedShell>
                                }
                              />
                              <Route
                                path={WORKSPACE_ROUTES.super}
                                element={<LegacyAdminRedirect />}
                              />
                              <Route
                                path="/workspace/learn/library"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.library}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/learn/quiz"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.exploreQuiz}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/manage"
                                element={<Navigate replace to="/workspace" />}
                              />
                              <Route
                                path="/workspace/positions"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.workbenchMonitor}
                                  />
                                }
                              />
                              <Route
                                path="/simulator"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.workbenchBuild}
                                  />
                                }
                              />
                              <Route
                                path="/backtests"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.explore}
                                  />
                                }
                              />
                              <Route
                                path="/backtests/:id"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.explore}
                                  />
                                }
                              />
                              <Route
                                path="/journal"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.library}
                                  />
                                }
                              />
                              <Route
                                path="/alerts"
                                element={
                                  <GatedShell>
                                    <Navigate
                                      replace
                                      to="/workspace#review-pane-alerts"
                                    />
                                  </GatedShell>
                                }
                              />
                              <Route
                                path="/library"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.library}
                                  />
                                }
                              />
                              <Route
                                path="/daily-quiz"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.exploreQuiz}
                                  />
                                }
                              />
                              <Route
                                path="/transactions"
                                element={
                                  <GatedShell>
                                    <TransactionsProvider>
                                      <Transactions />
                                    </TransactionsProvider>
                                  </GatedShell>
                                }
                              />
                              <Route
                                path="/positions"
                                element={
                                  <GatedShell>
                                    <Positions />
                                  </GatedShell>
                                }
                              />
                              <Route
                                path="/positions/:id"
                                element={
                                  <GatedShell>
                                    <PositionDetail />
                                  </GatedShell>
                                }
                              />
                              <Route
                                path="/settings"
                                element={
                                  <Navigate
                                    replace
                                    to={WORKSPACE_ROUTES.settings}
                                  />
                                }
                              />
                              <Route
                                path="/workspace/me/creator"
                                element={
                                  <RequireAuth>
                                    <AppShell lightweight>
                                      <CreatorVerification />
                                    </AppShell>
                                  </RequireAuth>
                                }
                              />
                              <Route
                                path="/kol"
                                element={
                                  <RequireAuth>
                                    <AppShell lightweight>
                                      <KolDashboardProvider>
                                        <KolDashboard />
                                      </KolDashboardProvider>
                                    </AppShell>
                                  </RequireAuth>
                                }
                              />
                              <Route
                                path="/admin"
                                element={<LegacyAdminRedirect />}
                              />
                              <Route
                                path="/admin/users"
                                element={<LegacyAdminRedirect tab="users" />}
                              />
                              <Route
                                path="/admin/notifications"
                                element={
                                  <LegacyAdminRedirect tab="notifications" />
                                }
                              />
                              <Route
                                path="/admin/kols"
                                element={<LegacyAdminRedirect tab="kols" />}
                              />
                              <Route
                                path="/manifold"
                                element={<PublicManifold />}
                              />
                              <Route
                                path="/compound"
                                element={<LegacyCompoundRedirect />}
                              />
                              <Route
                                path="/theta-nets"
                                element={
                                  <WorkspaceRedirect
                                    to={WORKSPACE_ROUTES.workbenchBuild}
                                  />
                                }
                              />
                              <Route path="/" element={<RootRedirect />} />
                              <Route path="/:slug" element={<KolLanding />} />
                              <Route path="*" element={<NotFound />} />
                            </Routes>
                            <InstallBanner />
                          </MarketingNavProvider>
                        </HolidaysProvider>
                      </TooltipProvider>
                    </PublicLearningTelemetryProvider>
                  </I18nProvider>
                </ThemeProvider>
              </TickerRegistryProvider>
            </UserChannelProvider>
          </TelemetryProvider>
        </AuthScopedTree>
      </ThemeBridge>
    </AuthProvider>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<div className="bg-background min-h-dvh w-full" />}>
      <AppRoutesContent />
    </Suspense>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <NuqsAdapter>
        <AppRoutes />
      </NuqsAdapter>
    </BrowserRouter>
  );
}
